// People (unified identity) - pure presentation and validation helpers, shared by the page and its
// tests. The server is the authority on every change; this only explains and pre-checks.
import type { AssignmentSpec, AssignmentView, DeliveryState, IdentityOptions, Person, SignInMethodType } from '../../services/identityPlatform';
import { ApiError } from '../../services/tenancy';

export const CAPABILITY_LABELS: Record<string, string> = {
  'tenant.view': 'See the customer',
  'network.configure': 'Configure networks',
  'departments.manage': 'Manage departments',
  'users.manage': 'Manage users',
  'radios.view': 'See radios',
  'radios.configure': 'Configure radios',
  'groups.view': 'See groups',
  'groups.manage': 'Manage groups',
  'dispatch.operate': 'Dispatch',
  'bookings.manage': 'Manage bookings',
  'billing.view': 'See billing',
};

export const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform administrator',
  platform_operator: 'Platform operator',
  oem_admin: 'OEM administrator',
  oem_user: 'OEM user',
  tenant_admin: 'Customer administrator',
  customer_user: 'Customer user',
};

export const ROLE_HINTS: Record<string, string> = {
  platform_admin: 'Everything, including platform security. Always requires MFA.',
  platform_operator: 'RadioOS operations everywhere - never platform security. Always requires MFA.',
  oem_admin: "Administers the OEM and every customer the OEM administers now.",
  oem_user: "Sees the OEM and its customers (read-only unless capabilities are given).",
  tenant_admin: 'Administers one customer.',
  customer_user: 'Works in one customer with the capabilities given, optionally only in some departments.',
};

export const METHOD_LABELS: Record<SignInMethodType, string> = {
  radioos: 'RadioOS password',
  microsoft: 'Microsoft',
  google: 'Google',
};

export function capabilityLabel(c: string): string {
  return CAPABILITY_LABELS[c] || c;
}

export function roleLabel(r: string): string {
  return ROLE_LABELS[r] || r.replace(/_/g, ' ');
}

/** An MFA-policy subject ("role:<role>" | "capability:<permission>") for display. */
export function subjectLabel(subject: string): string {
  const [kind, value] = subject.split(':');
  return kind === 'capability' ? `Capability: ${capabilityLabel(value)}` : roleLabel(value);
}

/** "Customer user - ACME (Dispatch, See radios; departments North, South)". */
export function describeAssignment(a: Pick<AssignmentView, 'role' | 'role_label' | 'oem_partner_name' | 'customer_name' | 'capabilities' | 'department_restricted' | 'departments'>): string {
  let out = a.role_label || roleLabel(a.role);
  const scope = a.oem_partner_name || a.customer_name;
  if (scope) out += ` - ${scope}`;
  const details: string[] = [];
  if (a.capabilities.length > 0) details.push(a.capabilities.filter((c) => c !== 'tenant.view').map(capabilityLabel).join(', ') || 'view only');
  if (a.department_restricted) {
    details.push(a.departments.length > 0 ? `departments ${a.departments.map((d) => d.name).join(', ')}` : 'NO departments left - no access');
  }
  return details.length > 0 ? `${out} (${details.join('; ')})` : out;
}

export function statusPresentation(p: Pick<Person, 'status' | 'invitation'>): { label: string; tone: 'green' | 'yellow' | 'red' | 'gray' } {
  switch (p.status) {
    case 'active':
      return { label: 'Active', tone: 'green' };
    case 'disabled':
      return { label: 'Disabled', tone: 'red' };
    default:
      if (p.invitation?.delivery_state === 'cancelled') return { label: 'Invitation cancelled', tone: 'gray' };
      if (p.invitation?.expired) return { label: 'Invitation expired', tone: 'yellow' };
      return { label: 'Invited', tone: 'yellow' };
  }
}

export function deliveryLabel(d: DeliveryState | undefined): string {
  switch (d) {
    case 'sent':
      return 'E-mailed (accepted for delivery)';
    case 'sending':
      return 'Sending e-mail';
    case 'failed':
      return 'E-mail failed - send a new invitation link';
    case 'not_configured':
      return 'Link handed over by the administrator (no e-mail configured)';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Pending';
  }
}

/** A draft assignment in the editor (before it is sent). */
export interface AssignmentDraft {
  role: string;
  scopeId: string;
  capabilities: string[];
  restricted: boolean;
  departments: string[];
}

export const emptyDraft = (): AssignmentDraft => ({ role: '', scopeId: '', capabilities: [], restricted: false, departments: [] });

export function scopeOfRole(options: IdentityOptions | null, role: string): 'platform' | 'oem' | 'customer' | '' {
  return options?.roles.find((r) => r.role === role)?.scope || '';
}

/** Validates a draft; returns the spec to send, or the reason it cannot be sent yet. */
export function draftToSpec(options: IdentityOptions | null, d: AssignmentDraft): { spec?: AssignmentSpec; problem?: string } {
  if (!d.role) return { problem: 'Choose a role.' };
  const scope = scopeOfRole(options, d.role);
  if (scope === '') return { problem: 'You cannot assign this role.' };
  if (scope === 'platform') return { spec: { role: d.role } };
  if (!d.scopeId) return { problem: scope === 'oem' ? 'Choose the OEM Partner.' : 'Choose the customer.' };
  if (scope === 'oem') {
    const spec: AssignmentSpec = { role: d.role, oem_partner_id: d.scopeId };
    if (d.role === 'oem_user' && d.capabilities.length > 0) spec.capabilities = d.capabilities;
    return { spec };
  }
  if (d.role === 'tenant_admin') return { spec: { role: d.role, customer_id: d.scopeId } };
  if (d.restricted && d.departments.length === 0) return { problem: 'Choose at least one department, or allow the whole customer.' };
  return {
    spec: { role: d.role, customer_id: d.scopeId, capabilities: d.capabilities, department_restricted: d.restricted, departments: d.restricted ? d.departments : [] },
  };
}

/** Which actions a person row offers (the server decides again). */
export function personActions(p: Person): { reissue: boolean; cancel: boolean; disable: boolean; enable: boolean; assign: boolean } {
  const can = p.manageable && !p.self && !p.legacy;
  const openInvitation = p.status === 'invited' && p.invitation?.delivery_state !== 'cancelled';
  return {
    reissue: can && p.status === 'invited',
    cancel: can && openInvitation,
    disable: can && p.status !== 'disabled',
    enable: can && p.status === 'disabled' && p.assignments.length > 0,
    assign: can && p.status !== 'disabled',
  };
}

/** Why Delete person is not available for a row (the server's reason code), or null. */
export const DELETE_BLOCKED: Record<string, string> = {
  SELF_DELETE_NOT_ALLOWED: 'You cannot delete yourself; another administrator must do it.',
  LAST_PLATFORM_ADMIN: 'This is the last platform administrator who can sign in; the platform must keep one.',
  USER_HAS_OUT_OF_SCOPE_ASSIGNMENTS: 'This person also has access outside your authority. Remove the access you administer instead.',
  OUTSIDE_DELEGATED_SCOPE: 'Deleting this person is outside your delegated authority.',
  LEGACY_USER: 'Legacy network account - delete it on the network Users page.',
  DELEGATION_DENIED: 'Deleting this person is outside your delegated authority.',
};

export function deleteBlockedReason(code?: string): string | null {
  if (!code) return null;
  return DELETE_BLOCKED[code] || 'You cannot delete this person.';
}

/** Whether the row shows "Delete person" at all, and whether it is enabled. A row the caller
 * cannot act on at all (own row, legacy, outside its authority) shows nothing; a person the caller
 * administers partly - or the last platform administrator - shows it disabled with the reason. */
export function deleteAction(p: Person): { show: boolean; enabled: boolean; reason: string | null } {
  if (p.deletable) return { show: true, enabled: true, reason: null };
  const explained = p.delete_blocked === 'USER_HAS_OUT_OF_SCOPE_ASSIGNMENTS' || p.delete_blocked === 'LAST_PLATFORM_ADMIN';
  return { show: explained, enabled: false, reason: deleteBlockedReason(p.delete_blocked) };
}

/** The confirmation of Delete person: who, how they sign in, what they can access, what happens. */
export function describePersonDelete(p: Person): { title: string; confirm: string; facts: [string, string][]; access: string[]; points: string[]; expected: string } {
  const name = p.display_name || p.email;
  const methods = p.methods.length > 0 ? p.methods.map((m) => METHOD_LABELS[m] || m).join(', ') : 'none';
  return {
    title: `Delete ${name}?`,
    confirm: 'Delete person permanently',
    facts: [['Name', p.display_name || '—'], ['E-mail', p.email || '—'], ['Status', statusPresentation(p).label], ['Sign-in methods', methods]],
    access: p.assignments.length > 0 ? p.assignments.map((a) => describeAssignment(a)) : ['No access'],
    points: [
      'All RadioOS sign-in methods are removed: the RadioOS password and authenticator app, recovery codes and the Microsoft / Google sign-in.',
      'All access listed above is removed, open invitation and reset links stop working, and every session ends immediately.',
      'Their Microsoft or Google account itself is not affected - only its connection to RadioOS is removed.',
      'Audit and security history stays and keeps referring to this person.',
      'This cannot be undone or re-enabled. Inviting the same person again creates a new RadioOS identity. To block sign-in temporarily, use Disable instead.',
    ],
    expected: (p.email || '').trim().toLowerCase(),
  };
}

/** A Delete person refusal: the server's message, its code, and what to do about it. */
export function deleteErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return 'The request could not be completed. Please try again.';
  const hint = err.code === 'REAUTHENTICATION_REQUIRED' ? ' Sign in to the Management Client with your RadioOS account (not a legacy token), then retry.'
    : err.code === 'STRONG_AUTHENTICATION_REQUIRED' ? ' Sign in with multi-factor authentication.'
      : err.code === 'USER_HAS_OUT_OF_SCOPE_ASSIGNMENTS' ? ' Use "remove" on the access you administer instead.' : '';
  return `${err.message}${err.code ? ` (${err.code})` : ''}.${hint}`.replace('..', '.');
}

/** The typed confirmation must be the person's e-mail address (case-insensitive). */
export function personDeleteConfirmed(typed: string, p: Person): boolean {
  const want = (p.email || '').trim().toLowerCase();
  return want !== '' && typed.trim().toLowerCase() === want;
}

export const INVITE_EXPLANATION = [
  'The person gets an invitation link (by e-mail when e-mail is configured; otherwise you see it once and hand it over).',
  'Until the invitation is accepted the person exists as INVITED and has no access at all.',
  'They choose how to sign in: Microsoft, Google or a RadioOS password with an authenticator app. The address must be theirs.',
  'An e-mail address never merges accounts: if the person already exists, add an assignment to them instead.',
];
